import React, { useState, useEffect } from 'react';
import { CommonInput } from '@/components/ui/common-input';
import { CommonSelect } from '@/components/ui/common-select';
import { CommonButton } from '@/components/ui/common-button';
import masterDataService from '@/services/masterDataService';
import rakePointService from '@/services/rakePointService';
import Loader from '@/components/ui/loader';

const EditRakePointForm = ({ rakePoint, onSubmit, onCancel, submitting }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    state_id: '',
    district_id: '',
    point_type: 'UNLOADING_POINT',
    is_active: true,
  });

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initForm = async () => {
      try {
        setLoading(true);
        // Load detailed RakePoint
        const detailed = await rakePointService.getRakePointById(rakePoint.id).catch(() => rakePoint);
        const stateData = await masterDataService.getStates(true).catch(() => []);
        const mappedStates = Array.isArray(stateData) ? stateData.map(s => ({ label: s.state_name || s.name || s.id, value: s.id })) : [];
        setStates(mappedStates);

        const resolvedDistrictId = detailed?.district_id || rakePoint?.district_id || '';
        let resolvedStateId = detailed?.district?.state_id || rakePoint?.district?.state_id || '';

        // Fallback state lookup if state_id not directly in object
        if (!resolvedStateId && resolvedDistrictId && mappedStates.length > 0) {
          for (const state of mappedStates) {
            try {
              const districtList = await masterDataService.getDistrictsByState(state.value, false);
              if (Array.isArray(districtList) && districtList.some(d => d.id === resolvedDistrictId)) {
                resolvedStateId = state.value;
                break;
              }
            } catch (e) {
              console.error('Failed state lookup for district:', e);
            }
          }
        }

        setFormData({
          name: detailed?.name || rakePoint?.name || '',
          code: detailed?.code || rakePoint?.code || '',
          state_id: resolvedStateId,
          district_id: resolvedDistrictId,
          point_type: detailed?.point_type || rakePoint?.point_type || 'UNLOADING_POINT',
          is_active: detailed?.is_active ?? rakePoint?.is_active ?? true,
        });

        if (resolvedStateId) {
          const districtData = await masterDataService.getDistrictsByState(resolvedStateId, true).catch(() => []);
          if (Array.isArray(districtData)) {
            setDistricts(districtData.map(d => ({ label: d.district_name || d.name || d.id, value: d.id })));
          }
        }
      } catch (err) {
        console.error('Failed to initialize edit form:', err);
      } finally {
        setLoading(false);
      }
    };

    if (rakePoint) initForm();
  }, [rakePoint]);

  const handleStateChange = async (stateId) => {
    setFormData(prev => ({ ...prev, state_id: stateId, district_id: '' }));
    if (formErrors.state_id) setFormErrors(prev => ({ ...prev, state_id: null }));

    if (!stateId) {
      setDistricts([]);
      return;
    }

    try {
      const districtData = await masterDataService.getDistrictsByState(stateId, true);
      if (Array.isArray(districtData)) {
        setDistricts(districtData.map(d => ({ label: d.district_name || d.name || d.id, value: d.id })));
      }
    } catch (err) {
      console.error('Failed to fetch districts:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Rake point name is required';
    if (!formData.state_id) errors.state_id = 'State is required';
    if (!formData.district_id) errors.district_id = 'District is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      name: formData.name.trim(),
      code: formData.code.trim() || null,
      district_id: formData.district_id || null,
      point_type: formData.point_type,
      is_active: formData.is_active,
    };

    onSubmit(payload);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <Loader size="md" message="Loading rake point details..." />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 py-2">
      <CommonInput
        label="Rake Point Name"
        placeholder="e.g. Haldia Rake Terminal 1"
        value={formData.name}
        onChange={(e) => {
          setFormData(prev => ({ ...prev, name: e.target.value }));
          if (formErrors.name) setFormErrors(prev => ({ ...prev, name: null }));
        }}
        error={formErrors.name}
        required
      />

      <CommonInput
        label="Rake Point Code (Optional)"
        placeholder="e.g. HRP-01"
        value={formData.code}
        onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
      />

      <CommonSelect
        label="Point Type"
        placeholder="Select Type"
        options={[
          { label: 'Unloading Point', value: 'UNLOADING_POINT' },
          { label: 'Godown (Buffer/Hub)', value: 'GODOWN' }
        ]}
        value={formData.point_type || 'UNLOADING_POINT'}
        onChange={(val) => setFormData(prev => ({ ...prev, point_type: val }))}
      />

      <CommonSelect
        label="State"
        placeholder="Select State"
        options={states}
        value={formData.state_id}
        onChange={handleStateChange}
        error={formErrors.state_id}
        required
        searchable
      />

      <CommonSelect
        label="District"
        placeholder={formData.state_id ? "Select District" : "Select State first"}
        options={districts}
        value={formData.district_id}
        onChange={(val) => {
          setFormData(prev => ({ ...prev, district_id: val }));
          if (formErrors.district_id) setFormErrors(prev => ({ ...prev, district_id: null }));
        }}
        disabled={!formData.state_id}
        error={formErrors.district_id}
        required
        searchable
      />

      <CommonSelect
        label="Status"
        placeholder="Select Status"
        options={[
          { label: 'Active', value: 'true' },
          { label: 'Inactive', value: 'false' }
        ]}
        value={formData.is_active ? 'true' : 'false'}
        onChange={(val) => setFormData(prev => ({ ...prev, is_active: val === 'true' }))}
      />

      <div className="flex justify-end space-x-2 pt-4 border-t">
        <CommonButton type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Cancel
        </CommonButton>
        <CommonButton type="submit" loading={submitting}>
          Update Rake Point
        </CommonButton>
      </div>
    </form>
  );
};

export default EditRakePointForm;
